import { Button, Label, ListBox, Select } from "@heroui/react";
import { uniqBy } from "lodash";
import { Plus, Trash } from "lucide-react";
import { useMemo } from "react";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { BannerProductPicker } from "@/components/banners/banner-product-picker";
import { ComboField, NamedOption } from "@/components/kit";
import { PromotionTargetType } from "@/enums/promotion";
import { useGetAllBrands } from "@/hooks/api/brand/useGetAllBrands";
import { useGetAllCategories } from "@/hooks/api/category/useGetAllCategories";
import { useGetAllSuppliers } from "@/hooks/api/supplier";
import { PromotionFormData } from "@/schemas/promotion-form.schema";
import { PROMOTION_TARGET_LABEL } from "@/utils/promotion";

export type NamedTargetType =
  | PromotionTargetType.CATEGORY
  | PromotionTargetType.SUB_CATEGORY
  | PromotionTargetType.BRAND
  | PromotionTargetType.SUPPLIER;

/** The API returns target ids only; names come from the admin's own lists. */
export const usePromotionTargetOptions = (): Record<
  NamedTargetType,
  NamedOption[]
> => {
  const { data: categories = [] } = useGetAllCategories();
  const { data: brands = [] } = useGetAllBrands();
  const { data: suppliers = [] } = useGetAllSuppliers();

  return useMemo(
    () => ({
      [PromotionTargetType.CATEGORY]: categories,
      [PromotionTargetType.SUB_CATEGORY]: uniqBy(
        categories.flatMap((category) => category.subCategories),
        "id",
      ),
      [PromotionTargetType.BRAND]: brands,
      [PromotionTargetType.SUPPLIER]: suppliers,
    }),
    [categories, brands, suppliers],
  );
};

const TARGET_TYPES = Object.values(PromotionTargetType);

const TargetRow = ({
  index,
  options,
  onRemove,
}: {
  index: number;
  options: Record<NamedTargetType, NamedOption[]>;
  onRemove: () => void;
}) => {
  const form = useFormContext<PromotionFormData>();
  const targetType = useWatch({
    control: form.control,
    name: `targets.${index}.targetType`,
  });

  return (
    <li className="bg-surface-secondary flex items-start gap-3 rounded-2xl p-3">
      <Controller
        control={form.control}
        name={`targets.${index}.targetType`}
        render={({ field }) => (
          <Select
            className="flex w-40 shrink-0 flex-col gap-1.5"
            selectedKey={field.value}
            variant="secondary"
            onSelectionChange={(key) => {
              field.onChange(String(key));
              form.setValue(`targets.${index}.targetId`, "", {
                shouldDirty: true,
              });
            }}
          >
            <Label>Applies to</Label>
            <Select.Trigger>
              <Select.Value />
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                {TARGET_TYPES.map((type) => (
                  <ListBox.Item
                    key={type}
                    id={type}
                    textValue={PROMOTION_TARGET_LABEL[type]}
                  >
                    {PROMOTION_TARGET_LABEL[type]}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                ))}
              </ListBox>
            </Select.Popover>
          </Select>
        )}
      />
      <div className="min-w-0 flex-1">
        {targetType === PromotionTargetType.ALL ? (
          <p className="text-muted pt-8 text-sm">Every product in the store</p>
        ) : (
          <Controller
            control={form.control}
            name={`targets.${index}.targetId`}
            render={({ field, fieldState }) =>
              targetType === PromotionTargetType.PRODUCT ? (
                <BannerProductPicker
                  errorMessage={fieldState.error?.message}
                  productId={field.value}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                />
              ) : (
                <ComboField
                  errorMessage={fieldState.error?.message}
                  isInvalid={fieldState.invalid}
                  items={options[targetType]}
                  label={PROMOTION_TARGET_LABEL[targetType]}
                  placeholder="Search by name"
                  value={field.value}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                />
              )
            }
          />
        )}
      </div>
      <Button
        isIconOnly
        aria-label="Remove target"
        className="mt-6 shrink-0"
        size="sm"
        variant="ghost"
        onPress={onRemove}
      >
        <Trash className="size-4" />
      </Button>
    </li>
  );
};

export const PromotionTargetPicker = () => {
  const form = useFormContext<PromotionFormData>();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "targets",
  });
  const options = usePromotionTargetOptions();
  const error = form.formState.errors.targets;
  const message = error?.message ?? error?.root?.message;

  return (
    <div className="flex flex-col gap-3">
      {fields.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {fields.map((field, index) => (
            <TargetRow
              key={field.id}
              index={index}
              options={options}
              onRemove={() => remove(index)}
            />
          ))}
        </ul>
      ) : null}
      {message ? (
        <p className="text-sm text-(--danger)" role="alert">
          {message}
        </p>
      ) : null}
      <Button
        className="w-fit"
        size="sm"
        variant="secondary"
        onPress={() =>
          append({ targetType: PromotionTargetType.PRODUCT, targetId: "" })
        }
      >
        <Plus className="size-4" />
        Add target
      </Button>
    </div>
  );
};
